<template>
  <div class="vpur">
    <APageHead
      title="바우처 · 구매 확정"
      sub="기관이 요청한 바우처 구매(충전)를 입금 확인 후 확정합니다. 요청만으로는 잔액이 오르지 않습니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="load">새로고침</ABtn>
      </template>
    </APageHead>

    <div class="p-tabs">
      <button
        v-for="tab in STATUS_TABS"
        :key="tab.value"
        type="button"
        :class="['p-tabs__tab', { 'is-active': status === tab.value }]"
        @click="switchStatus(tab.value)"
      >
        {{ tab.label }}
        <span v-if="status === tab.value && loaded" class="p-tabs__count">{{ total }}</span>
      </button>
    </div>

    <ACard overflow="visible">
      <div class="vpur__filters">
        <div class="p-field">
          <label class="p-field__label">기관</label>
          <select v-model="organizationIdx" class="p-input" @change="search">
            <option value="">전체 기관</option>
            <option v-for="o in orgOptions" :key="o.idx" :value="o.idx">{{ o.name }}</option>
          </select>
        </div>
      </div>
    </ACard>

    <ACard>
      <div v-if="loading" class="vpur__loading">
        <div class="p-spinner p-spinner--lg"></div>
      </div>
      <LoadError v-else-if="loadError" :message="loadError" @retry="load" />
      <AEmpty
        v-else-if="!items.length"
        :title="status === 'REQUESTED' ? '입금 대기 중인 요청이 없습니다' : '조회 결과가 없습니다'"
      />
      <template v-else>
        <ATable :columns="columns" :rows="items" row-key="idx">
          <template #cell:reference="{ row }">
            <div class="vpur__stack">
              <span class="vpur__mono vpur__strong">{{ row.reference || `VP-${row.idx}` }}</span>
              <span class="vpur__sub">{{ fmtKst(row.created_at) }}</span>
            </div>
          </template>
          <template #cell:organization="{ row }">
            <router-link
              v-if="row.organization_idx"
              class="vpur__link"
              :to="{ name: 'VoucherOrganizationDetail', params: { organizationIdx: row.organization_idx } }"
            >{{ row.organization_name || `#${row.organization_idx}` }}</router-link>
          </template>
          <template #cell:requested="{ row }">
            <div class="vpur__stack">
              <span v-if="row.requested_amount != null" class="vpur__mono vpur__strong">{{ money(row.requested_amount, row.requested_currency) }}</span>
              <span v-else class="vpur__sub">직접 등록</span>
              <span v-if="row.requested_by_name" class="vpur__sub">{{ row.requested_by_name }}</span>
              <span v-if="row.memo" class="vpur__sub" :title="row.memo">{{ row.memo }}</span>
            </div>
          </template>
          <template #cell:deposit="{ row }">
            <div v-if="row.status === 'CONFIRMED' || row.status === 'VOIDED'" class="vpur__stack">
              <span class="vpur__mono">{{ money(row.paid_amount, row.paid_currency) }}</span>
              <span
                v-if="row.credited_amount != null && row.paid_currency !== purchaseCurrency(row)"
                class="vpur__sub vpur__mono"
              >적립 {{ money(row.credited_amount, purchaseCurrency(row)) }}{{ row.rate_source === 'MANUAL' ? ' · 직접 입력' : row.rate_source === 'SYSTEM' ? ' · 시스템 환율' : '' }}</span>
              <span class="vpur__sub">{{ row.deposited_on || '' }}{{ row.payment_ref ? ` · ${row.payment_ref}` : '' }}</span>
            </div>
            <span v-else class="vpur__dash">—</span>
          </template>
          <template #cell:status="{ row }">
            <div class="vpur__stack vpur__stack--center">
              <APill :kind="purchaseStatusKind(row.status)" dot>{{ purchaseStatusLabel(row.status) }}</APill>
              <span v-if="row.cancel_reason" class="vpur__sub" :title="row.cancel_reason">
                {{ row.cancel_reason }}
              </span>
            </div>
          </template>
          <template #cell:actions="{ row }">
            <div class="vpur__actions">
              <template v-if="row.status === 'REQUESTED'">
                <ABtn size="sm" kind="primary" icon="check" @click="openConfirm(row)">입금 확인</ABtn>
                <ABtn size="sm" kind="ghost" icon="block" @click="reject(row)">반려</ABtn>
              </template>
              <ABtn
                v-else-if="row.status === 'CONFIRMED'"
                size="sm"
                kind="danger"
                icon="undo"
                title="오등록한 입금을 취소합니다. 적립액만큼 잔액이 빠집니다."
                @click="voidPurchase(row)"
              >확정 취소</ABtn>
            </div>
          </template>
        </ATable>
        <APagination :current-page="page" :total="total" :page-size="size" @change="onPage" />
      </template>
    </ACard>

    <PurchaseConfirmModal v-model="confirmOpen" mode="confirm" :purchase="confirmTarget" @done="load" />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import LoadError from "@/views/voucher/components/LoadError.vue";
import PurchaseConfirmModal from "@/views/voucher/components/PurchaseConfirmModal.vue";
import {
  fmtKst, money, pageOf, errorCode, errorMessage, loadErrorText,
  purchaseStatusLabel, purchaseStatusKind, purchaseCurrency,
} from "@/utils/voucher";

const STATUS_TABS = [
  { value: "REQUESTED", label: "입금 대기" },
  { value: "CONFIRMED", label: "확정" },
  { value: "CANCELLED", label: "반려 · 철회" },
  { value: "VOIDED", label: "확정 취소" },
  { value: "all", label: "전체" },
];

export default {
  name: "VoucherPurchases",
  components: { APageHead, ACard, ABtn, APill, ATable, AEmpty, APagination, PurchaseConfirmModal, LoadError },
  data() {
    return {
      STATUS_TABS,
      status: "REQUESTED",
      organizationIdx: "",
      orgOptions: [],
      items: [],
      total: 0,
      page: 1,
      size: 20,
      loading: false,
      loaded: false,
      loadError: "",
      confirmOpen: false,
      confirmTarget: null,
      columns: [
        { key: "reference", label: "요청", width: 150 },
        { key: "organization", label: "기관", width: 200 },
        { key: "requested", label: "요청 금액", width: 180 },
        { key: "deposit", label: "입금", width: 200 },
        { key: "status", label: "상태", width: 140, align: "center" },
        { key: "actions", label: "", width: 200, align: "right" },
      ],
    };
  },
  created() {
    // 기관 상세의 "구매 요청 N건" 배지에서 넘어오면 그 기관으로 좁혀 둡니다.
    if (this.$route.query.organization_idx) this.organizationIdx = String(this.$route.query.organization_idx);
    this.loadOrgOptions();
    this.load();
  },
  methods: {
    fmtKst,
    money,
    purchaseStatusLabel,
    purchaseStatusKind,
    purchaseCurrency,
    async loadOrgOptions() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/voucher/admin/organizations",
          params: { page: 1, size: 500 },
          loadingBar: false,
        });
        this.orgOptions = pageOf(res).items.map((o) => ({ idx: String(o.organization_idx), name: o.name }));
      } catch (e) {
        console.error("기관 목록 조회 실패:", e);
      }
    },
    switchStatus(v) {
      if (this.status === v) return;
      this.status = v;
      this.search();
    },
    search() {
      this.page = 1;
      this.load();
    },
    onPage(p) {
      this.page = p;
      this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const params = { status: this.status, page: this.page, size: this.size };
        if (this.organizationIdx) params.organization_idx = this.organizationIdx;
        const res = await this.$axios({ method: "get", url: "/voucher/admin/purchases", params, loadingBar: true });
        const p = pageOf(res, this.page);
        this.items = p.items;
        this.total = p.total;
        this.loaded = true;
      } catch (e) {
        console.error("구매 요청 조회 실패:", e);
        this.loadError = loadErrorText(e);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    openConfirm(row) {
      this.confirmTarget = row;
      this.confirmOpen = true;
    },
    async askReason(title, text, confirmText) {
      const r = await swal.fire({
        title,
        text,
        input: "textarea",
        inputPlaceholder: "사유 (필수)",
        inputValidator: (v) => (!v || !v.trim() ? "사유를 입력해 주세요." : undefined),
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: "취소",
      });
      return r.isConfirmed ? r.value.trim() : null;
    },
    async reject(row) {
      const reason = await this.askReason(
        "구매 요청 반려",
        `${row.reference || `VP-${row.idx}`} 요청을 반려합니다. 잔액은 바뀌지 않습니다.`,
        "반려",
      );
      if (!reason) return;
      try {
        await this.$axios({ method: "post", url: `/voucher/admin/purchases/${row.idx}/reject`, data: { reason }, loadingBar: true });
        swal.fire("반려했습니다", "", "success");
        this.load();
      } catch (e) {
        swal.fire("반려하지 못했습니다", errorMessage(e, "요청을 처리하지 못했습니다."), "error");
      }
    },
    async voidPurchase(row) {
      const reason = await this.askReason(
        "확정 취소 (오등록)",
        `${row.reference || `VP-${row.idx}`} 확정을 취소하면 적립한 ${money(row.credited_amount ?? row.paid_amount, purchaseCurrency(row))} 이 잔액에서 빠집니다.`,
        "확정 취소",
      );
      if (!reason) return;
      try {
        await this.$axios({ method: "post", url: `/voucher/admin/purchases/${row.idx}/void`, data: { reason }, loadingBar: true });
        swal.fire("확정을 취소했습니다", "", "success");
        this.load();
      } catch (e) {
        // 이미 학생 등록에 쓰여 잔액이 적립액보다 적으면 취소할 수 없습니다.
        if (errorCode(e) === "VOUCHER_BALANCE_SHORT") {
          swal.fire(
            "잔액이 모자랍니다",
            "이 입금액 중 일부가 이미 학생 등록에 쓰였습니다. 확정 취소 대신 기관 상세 › 잔액에서 반환·정정으로 처리해 주세요.",
            "warning",
          );
          return;
        }
        swal.fire("취소하지 못했습니다", errorMessage(e, "요청을 처리하지 못했습니다."), "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.vpur {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__filters {
    display: grid;
    grid-template-columns: minmax(200px, 320px);
    gap: 12px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 48px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    &--center { align-items: center; }
  }

  &__strong { font-weight: 700; color: $p-ink; }

  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  &__mono {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
  }

  &__dash { color: $p-ink-softer; }

  &__link {
    color: $p-info-ink;
    font-weight: 700;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }
}
</style>
